import React, { useState, useEffect } from 'react';
import MuiGrid from '../components/MuiGrid';
import { 
  Container, 
  Typography, 
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Card,
  CardContent,
  Avatar,
  TablePagination,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { supabase } from '../services/supabase';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupIcon from '@mui/icons-material/Group';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import GavelIcon from '@mui/icons-material/Gavel';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Game, League } from '../types/models';

const Games: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [, setGames] = useState<Game[]>([]);
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [leagueFilter, setLeagueFilter] = useState<string>('');
  const [winnerFilter, ] = useState<string>('');
  const [defaultLeagueSet, setDefaultLeagueSet] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalStats, setTotalStats] = useState({
    totalGames: 0,
    civilianWins: 0,
    mafiaWins: 0,
    tieGames: 0
  });
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [selectedGameResults, setSelectedGameResults] = useState<any[]>([]);
  const [loadingGameDetails, setLoadingGameDetails] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [currentComment, setCurrentComment] = useState("");
  const [modalTitle, setModalTitle] = useState("Comment");

  // Effect to run once on mount - we intentionally use empty deps here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // First, fetch leagues 
    const initializeApp = async () => {
      await fetchLeagues();
      
      // After leagues are loaded, check for game parameter
      const params = new URLSearchParams(location.search);
      const gameId = params.get('game');
      
      if (gameId) {
        // If we have a game ID, fetch details
        await fetchGameDetails(gameId);
      } else {
        // Clear selected game when no game ID is in URL
        setSelectedGame(null);
        setSelectedGameResults([]);
      }
    };
    
    initializeApp();
  }, []); // Empty dependency array means this runs once on mount
  
  // Handle location changes after initial mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gameId = params.get('game');
    
    if (gameId) {
      fetchGameDetails(gameId);
    } else {
      // Clear selected game when no game ID is in URL
      setSelectedGame(null);
      setSelectedGameResults([]);
    }
  }, [location]);

  // This effect will run immediately when a league is set (initial or changed)
  useEffect(() => {
    if (leagueFilter) {
      fetchGames();
      fetchGamesStats();
    }
  }, [leagueFilter, winnerFilter]);
  
  useEffect(() => {
    fetchGames();
  }, [page, rowsPerPage]);

  const fetchLeagues = async () => {
    try {
      const { data, error } = await supabase
        .from('leagues')
        .select(`
          id,
          name,
          club:club_id (
            id,
            name
          )
        `)
        .order('name', { ascending: true });

      if (error) throw error;
      
      // Transform data to match League type with safer property access
      const formattedLeagues = (data || []).map(item => {
        // Safely cast item as any to extract properties
        const itemAny = item as any;
        
        // Create a proper League object with required fields
        return {
          id: itemAny.id || '',
          club_id: itemAny.club_id || '', // Use actual club_id if available
          judge_id: itemAny.judge_id || '', // Add judge_id field with default
          name: itemAny.name || '',
          created_at: new Date().toISOString(),
          // Provide a valid Club sub-object
          club: {
            id: itemAny.club?.id || '',
            name: itemAny.club?.name || '',
            created_at: new Date().toISOString()
          }
        };
      });
      setLeagues(formattedLeagues);
      
      // Set default league if not already set and leagues exist
      if (!defaultLeagueSet && formattedLeagues.length > 0) {
        setLeagueFilter(formattedLeagues[0].id);
        setDefaultLeagueSet(true);
      }
    } catch (error: any) {
      console.error('Error fetching leagues:', error.message);
    }
  };

  const fetchGamesStats = async () => {
    try {
      // If no league filter is set, don't fetch stats
      if (!leagueFilter) {
        setTotalStats({
          totalGames: 0,
          civilianWins: 0,
          mafiaWins: 0,
          tieGames: 0
        });
        return;
      }
      
      // Try to use the stored procedure with league filter
      const { data, error } = await supabase
        .rpc('get_league_stats', { league_id_param: leagueFilter } as any);
      
      if (!error && data && data.length > 0) {
        setTotalStats({
          totalGames: data[0]?.total_games || 0,
          civilianWins: data[0]?.civilian_wins || 0,
          mafiaWins: data[0]?.mafia_wins || 0,
          tieGames: data[0]?.tie_games || 0
        });
        return;
      }
      
      // Fallback: Filter games manually if the RPC fails
      let gamesQuery = supabase.from('games')
        .select('winner')
        .eq('league_id', leagueFilter); // Always apply league filter
      
      if (winnerFilter) {
        gamesQuery = gamesQuery.eq('winner', winnerFilter as 'CIVILIANS' | 'MAFIA' | 'TIE');
      }
      
      const { data: gamesData, error: gamesError } = await gamesQuery;

      if (gamesError) throw gamesError;

      const total = gamesData?.length || 0;
      const civilianWins = gamesData?.filter(g => g.winner === 'CIVILIANS').length || 0;
      const mafiaWins = gamesData?.filter(g => g.winner === 'MAFIA').length || 0;
      const tieGames = gamesData?.filter(g => g.winner === 'TIE').length || 0;

      setTotalStats({
        totalGames: total,
        civilianWins,
        mafiaWins,
        tieGames
      });
    } catch (error: any) {
      console.error('Error fetching game stats:', error.message);
    }
  };

  const fetchGames = async () => {
    setLoading(true);
    try {
      // If no league is selected yet and we have leagues, set the first one as default
      if (!leagueFilter && leagues.length > 0 && !defaultLeagueSet) {
        setLeagueFilter(leagues[0].id);
        setDefaultLeagueSet(true);
        return; // This will trigger the useEffect to call fetchGames again with the league filter
      }
      
      // Must have a league filter to continue
      if (!leagueFilter) {
        // No league filter and no leagues available yet
        setGames([]);
        setFilteredGames([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }
      
      // Build the query
      let query = supabase
        .from('games')
        .select(`
          *,
          league:league_id (
            id,
            name,
            club:club_id (
              id,
              name
            )
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `, { count: 'exact' })
        // Always apply league filter
        .eq('league_id', leagueFilter);

      // Apply winner filter if selected
      if (winnerFilter) {
        query = query.eq('winner', winnerFilter as any);
      }

      // Apply pagination with ordering by session and game number
      query = query
        .order('session_num', { ascending: false })  // First by session number (descending)
        .order('number_in_session', { ascending: false })  // Then by game number (descending)
        .range(page * rowsPerPage, (page + 1) * rowsPerPage - 1);

      const { data, error, count } = await query;

      if (error) throw error;
      setGames(data || []);
      setFilteredGames(data || []);
      setTotalCount(count || 0);
    } catch (error: any) {
      console.error('Error fetching games:', error.message);
    } finally {
      setLoading(false);
    }
  };
  const handleLeagueFilterChange = (event: SelectChangeEvent) => {
    // Prevent selection of empty value (no league filter)
    if (event.target.value !== '') {
      setLeagueFilter(event.target.value);
      setPage(0); // Reset to first page when filter changes
    }
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchGameDetails = async (gameId: string) => {
    setLoadingGameDetails(true);
    try {
      // Fetch the specific game with detailed information
      const { data: gameData, error: gameError } = await supabase
        .from('games')
        .select(`
          *,
          league:league_id (
            id,
            name,
            club:club_id (
              id,
              name
            )
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `)
        .eq('id', gameId)
        .single();

      if (gameError) throw gameError;
      
      if (gameData) {
        setSelectedGame(gameData);
        
        // Always update the league filter to match the game's league
        // This ensures consistent filtering when navigating back
        if (gameData.league_id) {
          setLeagueFilter(gameData.league_id);
          setDefaultLeagueSet(true);
        }
        
        // Fetch game results for this game
        const { data: resultsData, error: resultsError } = await supabase
          .from('game_results')
          .select(`
            *,
            player:player_id (
              id,
              nickname,
              full_name
            )
          `)
          .eq('game_id', gameId)
          .order('player_num', { ascending: true });
          
        if (resultsError) throw resultsError;
        setSelectedGameResults(resultsData || []);
      }
    } catch (error: any) {
      console.error('Error fetching game details:', error.message);
    } finally {
      setLoadingGameDetails(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getWinnerChip = (winner: string) => {
    switch (winner) {
      case 'CIVILIANS':
        return <Chip icon={<GroupIcon />} label="CIVILIANS" color="primary" size="small" />;
      case 'MAFIA':
        return <Chip icon={<SportsKabaddiIcon />} label="MAFIA" color="error" size="small" />;
      case 'TIE':
        return <Chip label="TIE" color="default" size="small" />;
      default:
        return null;
    }
  };
  
  const getRoleChip = (role: string) => {
    switch (role) {
      case 'CITIZEN':
        return <Chip icon={<GroupIcon />} label="CITIZEN" color="primary" size="small" />;
      case 'MAFIA':
        return <Chip icon={<SportsKabaddiIcon />} label="MAFIA" color="error" size="small" />;
      case 'SHERIFF':
        return <Chip icon={<LocalPoliceIcon />} label="SHERIFF" color="success" size="small" />;
      case 'DON':
        return <Chip icon={<TheaterComedyIcon />} label="DON" color="warning" size="small" />;
      default:
        return null;
    }
  };
  
  const formatWinningMove = (moveString: string | null): string => {
    if (!moveString) return 'N/A';
    
    const moves = moveString.split(',');
    if (moves.length !== 3) return moveString;
    
    return `${moves.join(', ')}`;
  };
  
  const handleWinningMoveClick = (e: React.MouseEvent, moveString: string | null) => {
    e.stopPropagation();
    setCurrentComment(formatWinningMove(moveString || ''));
    setModalTitle("Winning Move");
    setCommentModalOpen(true);
  };
  
  const handleCommentClick = (e: React.MouseEvent, comment: string) => {
    e.stopPropagation();
    setCurrentComment(comment);
    setModalTitle("Comment");
    setCommentModalOpen(true);
  };
  
  const handleBackToList = () => {
    // This will just clear the game query parameter but keep us on the same page
    // which maintains the league filter state
    navigate('/games', { replace: true });
  };

  return (
    <Container maxWidth="lg">
      {/* Comment Modal */}
      <Dialog 
        open={commentModalOpen} 
        onClose={() => setCommentModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{modalTitle}</Typography>
          <Button 
            onClick={() => setCommentModalOpen(false)} 
            sx={{ minWidth: 'auto', p: 0.5 }}
            color="inherit"
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Typography>{currentComment}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ my: 4 }}>
        {selectedGame ? (
          // Game details view
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBackToList}
                variant="outlined"
                sx={{ mr: 2 }}
              >
                Back to Games
              </Button>
              <Typography variant="h4" component="h1">
                Session {selectedGame.session_num}, Game {selectedGame.number_in_session}
              </Typography>
            </Box>
            
            {loadingGameDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" component="h2">
                      Game Details
                    </Typography>
                    {getWinnerChip(selectedGame.winner)}
                  </Box>
                  
                  <MuiGrid container spacing={3}>
                    <MuiGrid item xs={12} sm={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">League</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {(selectedGame.league as any)?.name || 'Unknown League'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(selectedGame.league as any)?.club?.name || 'Unknown Club'}
                        </Typography>
                      </Box>
                    </MuiGrid>
                    
                    <MuiGrid item xs={12} sm={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Date</Typography>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarMonthIcon sx={{ mr: 1, fontSize: 18 }} />
                          {formatDate(selectedGame.occurred_at)}
                        </Typography>
                      </Box>
                    </MuiGrid>
                    
                    <MuiGrid item xs={12} sm={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Judge</Typography>
                        <Chip 
                          icon={<GavelIcon />}
                          label={(selectedGame.judge as any)?.nickname || 'Unknown'}
                          color="secondary"
                          variant="outlined"
                          onClick={() => navigate(`/player/${selectedGame.judge_id}`)}
                          clickable
                        />
                      </Box>
                    </MuiGrid>

                    <MuiGrid item xs={12} sm={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Video</Typography>
                        {selectedGame.video_link ? (
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<VideocamIcon />}
                            onClick={() => selectedGame.video_link && window.open(selectedGame.video_link, '_blank')}
                            sx={{ mt: 0.5 }}
                          >
                            Watch
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No video available
                          </Typography>
                        )}
                      </Box>
                    </MuiGrid>
                  </MuiGrid>
                </Paper>
                
                <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
                  Players
                </Typography>
                
                {selectedGameResults.length > 0 ? (
                  <Paper sx={{ borderRadius: 2 }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Player</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Bonus</TableCell>
                            <TableCell>Notes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedGameResults.map((result) => (
                            <TableRow 
                              key={result.id}
                            >
                              <TableCell>{result.player_num}</TableCell>
                              <TableCell>
                                <Button
                                  onClick={() => navigate(`/player/${result.player_id}`)}
                                  sx={{ 
                                    textAlign: 'left',
                                    display: 'block',
                                    minWidth: 'auto',
                                    p: 0.5,
                                    borderRadius: 1,
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 255, 255, 0.08)'
                                    }
                                  }}
                                >
                                  <Typography variant="body2" fontWeight="medium">
                                    {(result.player as any)?.nickname || 'Unknown Player'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {(result.player as any)?.full_name || ''}
                                  </Typography>
                                </Button>
                              </TableCell>
                              <TableCell>{getRoleChip(result.role)}</TableCell>
                              <TableCell>
                                {result.is_win ? (
                                  <Chip 
                                    icon={<EmojiEventsIcon />}
                                    label="Win" 
                                    color="success"
                                    size="small"
                                    variant="filled"
                                  />
                                ) : null}
                              </TableCell>
                              <TableCell>
                                <Stack direction="column" spacing={1} alignItems="flex-start">
                                  {result.bonus_points !== 0 && (
                                    <Chip 
                                      label={`${result.bonus_points > 0 ? '+' : ''}${result.bonus_points}`}
                                      size="small"
                                      color={result.bonus_points > 0 ? "success" : "error"}
                                    />
                                  )}
                                  {result.is_killed_first && result.winning_move && (result.role === 'CITIZEN' || result.role === 'SHERIFF') && (
                                    <Box>
                                      {(() => {
                                        // Parse the winning move to get the correct number of mafia identified
                                        let guessedMafiaNumbers = [];
                                        try {
                                          if (result.winning_move.includes(',')) {
                                            guessedMafiaNumbers = result.winning_move.split(',').map((n: string) => parseInt(n.trim(), 10));
                                          } else if (result.winning_move.includes(' ')) {
                                            guessedMafiaNumbers = result.winning_move.split(' ').map((n: string) => parseInt(n.trim(), 10));
                                          } else {
                                            guessedMafiaNumbers = result.winning_move.match(/\d+/g)?.map((n: string) => parseInt(n, 10)) || [];
                                          }
                                          
                                          // Find the actual mafia players in this game
                                          const allPlayersInGame = selectedGameResults || [];
                                          const mafiaPlayers = allPlayersInGame.filter(gr => 
                                            gr.role === 'MAFIA' || gr.role === 'DON'
                                          );
                                          
                                          // Count how many guesses were correct
                                          const correctGuesses = guessedMafiaNumbers.filter((playerNum: number) => 
                                            mafiaPlayers.some(mp => mp.player_num === playerNum)
                                          ).length;
                                          
                                          if (correctGuesses === 2) {
                                            return (
                                              <Chip 
                                                icon={<GpsFixedIcon />}
                                                label="2 | +0.5"
                                                size="small"
                                                color="secondary"
                                              />
                                            );
                                          } else if (correctGuesses === 3) {
                                            return (
                                              <Chip 
                                                icon={<GpsFixedIcon />}
                                                label="3 | +0.8"
                                                size="small"
                                                color="secondary"
                                              />
                                            );
                                          }
                                        } catch (e) {
                                          return null;
                                        }
                                        return null;
                                      })()}
                                    </Box>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                  {result.is_killed_first && (
                                    <Chip 
                                      icon={<PersonOffIcon />}
                                      label="First Kill" 
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                    />
                                  )}
                                  {result.winning_move && (
                                    <Chip 
                                      label={`Move: ${formatWinningMove(result.winning_move)}`}
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                      onClick={(e) => handleWinningMoveClick(e, result.winning_move)}
                                    />
                                  )}
                                  {result.comment && (
                                    <Tooltip title={result.comment} arrow placement="top">
                                      <Chip
                                        icon={<InfoIcon />}
                                        label={result.comment.length > 18 ? result.comment.substring(0, 15) + '...' : result.comment}
                                        size="small"
                                        variant="outlined"
                                        sx={{ maxWidth: '300px' }}
                                        onClick={(e) => handleCommentClick(e, result.comment)}
                                      />
                                    </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ) : (
                  <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                    <Typography color="text.secondary">
                      No player data available for this game.
                    </Typography>
                  </Paper>
                )}
              </>
            )}
          </>
        ) : (
          // Games list view
          <>
            <Typography variant="h4" component="h1" gutterBottom>
              Games
            </Typography>
            
            {/* Stats Summary - only show on list view */}
            {!selectedGame && (
              <MuiGrid container spacing={3} sx={{ mb: 4 }}>
                <MuiGrid item xs={6} sm={3}>
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(145deg, rgba(92, 107, 192, 0.15), rgba(92, 107, 192, 0.3))',
                    color: 'white',
                    border: '1px solid rgba(92, 107, 192, 0.2)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                          {totalStats.totalGames}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" component="div">
                            Total Games
                          </Typography>
                          <Typography variant="caption">
                            In this league
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </MuiGrid>
                
                <MuiGrid item xs={6} sm={3}>
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(145deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0.3))',
                    color: 'white',
                    border: '1px solid rgba(76, 175, 80, 0.2)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                          <GroupIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" component="div">
                            {totalStats.civilianWins}
                          </Typography>
                          <Typography variant="caption">
                            {totalStats.totalGames > 0 
                              ? ((totalStats.civilianWins / totalStats.totalGames) * 100).toFixed(1) + '%'
                              : '0%'} Civilian Wins
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </MuiGrid>
                
                <MuiGrid item xs={6} sm={3}>
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(145deg, rgba(244, 67, 54, 0.15), rgba(244, 67, 54, 0.3))',
                    color: 'white',
                    border: '1px solid rgba(244, 67, 54, 0.2)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                          <SportsKabaddiIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" component="div">
                            {totalStats.mafiaWins}
                          </Typography>
                          <Typography variant="caption">
                            {totalStats.totalGames > 0 
                              ? ((totalStats.mafiaWins / totalStats.totalGames) * 100).toFixed(1) + '%'
                              : '0%'} Mafia Wins
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </MuiGrid>
                
                <MuiGrid item xs={6} sm={3}>
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(145deg, rgba(158, 158, 158, 0.15), rgba(158, 158, 158, 0.3))',
                    color: 'white',
                    border: '1px solid rgba(158, 158, 158, 0.2)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'grey.600', mr: 2 }}>
                          {totalStats.tieGames}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" component="div">
                            Tie Games
                          </Typography>
                          <Typography variant="caption">
                            {totalStats.totalGames > 0 
                              ? ((totalStats.tieGames / totalStats.totalGames) * 100).toFixed(1) + '%'
                              : '0%'} of total
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </MuiGrid>
              </MuiGrid>
            )}
            
            {/* Filters - only show on list view */}
            {!selectedGame && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" component="h3" gutterBottom>
                  Select League
                </Typography>
                <FormControl fullWidth variant="outlined" size="medium">
                  <InputLabel id="league-filter-label">League</InputLabel>
                  <Select
                    labelId="league-filter-label"
                    id="league-filter"
                    value={leagueFilter}
                    onChange={handleLeagueFilterChange}
                    label="League"
                    sx={{ 
                      height: 56,
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                    required
                  >
                    {leagues.map((league) => (
                      <MenuItem key={league.id} value={league.id}>
                        {league.name} ({(league.club as any)?.name})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            )}
          </>
        )}
        
        {!selectedGame && (
          <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden', borderRadius: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Game</TableCell>
                        <TableCell>League</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Judge</TableCell>
                        <TableCell>Winner</TableCell>
                        <TableCell>Video</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredGames.map((game) => (
                        <TableRow 
                          key={game.id} 
                          hover
                          onClick={() => navigate(`/games?game=${game.id}`)}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)'
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body1">
                              Session {game.session_num}, Game {game.number_in_session}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button 
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click event
                                navigate(`/league/${game.league_id}`);
                              }}
                              sx={{ 
                                p: 0.5, 
                                textAlign: 'left',
                                color: 'text.primary',
                                display: 'block',
                                minWidth: 'auto',
                                borderRadius: 1,
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            >
                              <Typography variant="body2" fontWeight="medium">
                                {(game.league as any)?.name || 'Unknown League'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(game.league as any)?.club?.name || 'Unknown Club'}
                              </Typography>
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CalendarMonthIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                              {formatDate(game.occurred_at)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={<GavelIcon />} 
                              label={(game.judge as any)?.nickname || 'Unknown'} 
                              size="small"
                              color="secondary"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click event
                                navigate(`/player/${game.judge_id}`);
                              }}
                              clickable
                              sx={{ cursor: 'pointer' }}
                            />
                          </TableCell>
                          <TableCell>
                            {getWinnerChip(game.winner)}
                          </TableCell>
                          <TableCell>
                            {game.video_link ? (
                              <Tooltip title="Watch video">
                                <IconButton 
                                  edge="end" 
                                  color="primary"
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation(); // Prevent row click
                                    if (game.video_link) window.open(game.video_link, '_blank');
                                  }}
                                  size="small"
                                >
                                  <VideocamIcon />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {filteredGames.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography variant="body1" color="text.secondary">
                              No games found matching your criteria.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default Games;